import { Box, Container, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Header } from "../../../components/Header";
import UserAvatar from "../../../components/UserAvatar";
import { colorGrid } from "../../../theme";
import { useUserData } from "../../../user";
import { useUserActivity } from "../../../utils/contexts/users/userActivityContext";
import { useLocalState } from "../../../utils/hooks/global/useLocalStorage";
import CustomTabsTable from "../../global/CustomTabsTable";
import { Loading } from "../../global/Loading";
import UserRoleButton from "./UserRoleButton";

const roleOf = (user) =>
  user.isSuperadmin ? "Superadmin" : user.isAdmin ? "Admin" : "User";

const ROLE_COLOR = { Superadmin: "blue", Admin: "yellow", User: "gray" };

function AdminUsers() {
  const [jwt] = useLocalState(null, "jwt");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { userStatus } = useUserActivity();
  const currentUser = useUserData();

  const handleRoleChange = (updated) => {
    setUsers((prev) =>
      prev.map((user) =>
        user.id === updated.id ? { ...user, isAdmin: updated.isAdmin } : user
      )
    );
  };

  useEffect(() => {
    if (userStatus) {
      const newAllUsers = users.map((user) => {
        if (user.id === userStatus?.userId) {
          return {
            ...user,
            isOnline: userStatus?.isOnline,
            lastActive: userStatus?.lastActive,
          };
        }
        return user;
      });
      setUsers(newAllUsers);
    }
  }, [userStatus]);

  const tabsFunctions = {
    all: {
      func: function (data) {
        return data;
      },
      label: "All",
      color: "default",
    },
    isOnline: {
      func: function (data) {
        return data.filter((user) => user.isOnline);
      },
      label: "Active",
      color: "green",
    },
    isAdmin: {
      func: function (values) {
        return values.filter((user) => user.isAdmin);
      },
      label: "Admin",
      color: "yellow",
    },
    offline: {
      func: function (values) {
        return values.filter((user) => !user.isOnline);
      },
      label: "Offline",
      color: "gray",
    },
  };

  useEffect(() => {
    fetch(`${backendUrl}/users?includeProfiles=true`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + jwt,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setUsers(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("There was an error!", error);
      });
  }, [jwt, backendUrl]);

  const columns = [
    {
      field: "displayName",
      headerName: "Name",
      renderCell: (params) => {
        return (
          <Box
            key={params.id}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <UserAvatar
              name={params.row.displayName}
              size={28}
              sx={{ mr: 1.25 }}
            />
            {params.formattedValue}
          </Box>
        );
      },
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "lastActive",
      headerName: "Last seen",
      flex: 1,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        const formattedDate = date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "UTC",
        });
        return formattedDate;
      },
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.6,
      minWidth: 110,
      valueGetter: (params) => roleOf(params.row),
      renderCell: (params) => {
        const color = colorGrid[ROLE_COLOR[params.value]];
        return (
          <Box
            component="span"
            sx={{
              bgcolor: color.background,
              color: color.text,
              borderRadius: "4px",
              px: 0.75,
              py: 0.25,
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    // Only superadmins change roles; superadmins themselves and your own
    // role are managed elsewhere
    ...(currentUser?.isSuperadmin
      ? [
          {
            field: "actions",
            headerName: "",
            sortable: false,
            filterable: false,
            flex: 0.4,
            align: "right",
            renderCell: (params) =>
              params.row.isSuperadmin ||
              params.row.id === currentUser.id ? null : (
                <UserRoleButton
                  user={params.row}
                  onChange={handleRoleChange}
                />
              ),
          },
        ]
      : []),
  ];
  return (
    <>
      {" "}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "75vh",
          }}
        >
          <Loading size={50} thickness={6} />
        </Box>
      ) : (
        <Container maxWidth={"xl"}>
          <Header title="Users" />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomTabsTable
                columns={columns}
                tabs={tabsFunctions}
                name={"adminUsers"}
                data={users}
              />
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
}

export default AdminUsers;
